<template>
  <SlideOverTray :open="!!itemId" @close="$emit('close')">
    <div class="flex flex-col h-full overflow-hidden">
        <!-- Loading skeleton -->
        <div v-if="loading" class="flex flex-col animate-pulse">
          <div class="px-5 py-4 border-b border-gray-200">
            <div class="flex items-center gap-2 mb-2">
              <div class="h-5 w-16 bg-gray-200 rounded"></div>
              <div class="h-5 w-20 bg-gray-200 rounded"></div>
            </div>
            <div class="h-6 w-3/4 bg-gray-200 rounded"></div>
          </div>
          <div class="px-5 py-5 space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div v-for="n in 4" :key="n">
                <div class="h-3 w-16 bg-gray-100 rounded mb-1.5"></div>
                <div class="h-4 w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div>
              <div class="h-3 w-20 bg-gray-100 rounded mb-1.5"></div>
              <div class="h-20 bg-gray-100 rounded"></div>
            </div>
          </div>
        </div>

        <template v-else>
          <!-- Header -->
          <div class="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white shrink-0">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span
                  class="text-[11px] font-medium px-1.5 py-0.5 rounded"
                  :class="typeClass(item.requirement_type_name)"
                >
                  {{ typeLabel(item.requirement_type_name) }}
                </span>
                <!-- role="img" is load-bearing: a bare <span> is role=generic,
                     where aria-label is prohibited and dropped by every screen
                     reader, so the gloss would never be announced. -->
                <span
                  class="text-xs px-2 py-0.5 rounded"
                  :class="statusClass(item.status)"
                  :title="statusTitle(item.status)"
                  role="img"
                  :aria-label="statusAriaLabel(item.status)"
                >
                  {{ statusLabel(item.status) }}
                </span>
                <SourceBadge v-if="item.source" :source="item.source" />
                <button
                  type="button"
                  class="text-[11px] font-mono transition-colors"
                  :class="idCopied ? 'text-green-600' : 'text-gray-400 hover:text-primary-600'"
                  :title="idCopied ? 'Copied!' : 'Copy ticket number'"
                  :aria-label="'Copy ticket number ' + item.id"
                  @click="copyTicketId"
                >#{{ item.id }}</button>
              </div>
              <button
                class="text-gray-400 hover:text-gray-600 p-1"
                @click="$emit('close')"
              >
                <FontAwesomeIcon :icon="['fas', 'times']" />
              </button>
            </div>
            <h2 class="text-lg font-semibold text-gray-900 break-words">{{ item.title }}</h2>
            <a
              :href="ticketMailtoHref"
              class="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              <FontAwesomeIcon :icon="['fas', 'envelope']" class="text-[10px]" />
              Email about this ticket
            </a>
            <!-- Staff-only forward address: lets staff route customer emails
                 from Pylon threads into this ticket by forwarding to this
                 address. Absent when SUPPORT_REPLY_SECRET is not configured
                 server-side (field stripped from the serializer). -->
            <div v-if="isStaffView && item.reply_address" class="flex items-center gap-1.5 mt-2 text-[11px]">
              <FontAwesomeIcon :icon="['fas', 'at']" class="text-[10px] text-gray-400 shrink-0" />
              <span class="text-gray-500">Forward customer emails here:</span>
              <code class="font-mono text-primary-600 truncate">{{ item.reply_address }}</code>
              <button
                type="button"
                class="shrink-0 font-medium transition-colors"
                :class="replyAddressCopied ? 'text-green-600' : 'text-gray-400 hover:text-primary-600'"
                :title="replyAddressCopied ? 'Copied!' : 'Copy forward address'"
                :aria-label="'Copy forward address for ticket #' + item.id"
                @click="copyReplyAddress"
              >
                <FontAwesomeIcon :icon="['fas', replyAddressCopied ? 'check' : 'copy']" class="mr-0.5" />
                {{ replyAddressCopied ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <div v-if="isStaffView" class="flex items-center gap-2 mt-2">
              <span
                v-if="item.customer_facing !== false"
                class="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
              >
                <FontAwesomeIcon :icon="['fas', 'eye']" class="text-[9px]" />
                Customer can see this
              </span>
              <span
                v-else
                class="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500"
              >
                <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[9px]" />
                Internal only — the customer can't see this
              </span>
              <div class="relative">
                <button
                  class="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
                  @click.stop="toggleCustomerLegend"
                >
                  <FontAwesomeIcon :icon="['fas', 'circle-info']" class="text-[9px]" />
                  {{ isInternalOnly ? 'What this means' : 'What the customer sees' }}
                </button>
                <div
                  v-if="showCustomerLegend"
                  class="absolute z-20 top-full left-0 mt-1.5 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left"
                  @click.stop
                >
                  <!-- An internal-only ticket isn't in the customer's Service
                       Desk at all, so listing which of its fields are "visible
                       to the customer" contradicts the badge right next to it. -->
                  <template v-if="isInternalOnly">
                    <p class="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5 mb-1.5">
                      <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px]" />
                      The customer sees none of this
                    </p>
                    <p class="text-[11px] text-gray-600">
                      This ticket is marked internal, so it doesn't appear in the
                      customer's Service Desk at all — not the title, the
                      description, or the status. Only your team can see it.
                    </p>
                  </template>
                  <template v-else>
                    <p class="text-[11px] font-semibold text-emerald-700 flex items-center gap-1.5 mb-1.5">
                      <FontAwesomeIcon :icon="['fas', 'eye']" class="text-[10px]" />
                      Visible to the customer
                    </p>
                    <ul class="text-[11px] text-gray-600 space-y-0.5 mb-3 pl-4 list-disc">
                      <li>Title and description</li>
                      <li>Due date, requester, created/updated</li>
                      <li>
                        Status, in customer-friendly wording — <strong>Waiting</strong>
                        reads as "Needs Input" and asks them to reply
                      </li>
                      <li>Replies to their comments</li>
                    </ul>
                    <p class="text-[11px] font-semibold text-amber-700 flex items-center gap-1.5 mb-1.5">
                      <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px]" />
                      Never shown to the customer
                    </p>
                    <ul class="text-[11px] text-gray-600 space-y-0.5 pl-4 list-disc">
                      <li>Internal notes</li>
                      <li>Owner, priority and due-date changes</li>
                      <li>The Activity log (staff-only, enforced by the API)</li>
                      <li>Comments marked as internal notes</li>
                    </ul>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Scrollable content -->
          <div class="flex-1 overflow-y-auto">
            <div class="px-5 py-5 space-y-5">
              <!-- Triage panel -->
              <TriagePanel
                v-if="item.needs_triage && isStaffView"
                :requirement-id="item.id"
                :sender-email="firstEmailSender"
                @triaged="onTriaged"
              />

              <!-- Meta fields -->
              <div class="grid grid-cols-2 gap-x-6 gap-y-2.5">
                <div v-if="item.requester_name">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Requester</label>
                  <div class="text-sm text-gray-900">{{ item.requester_name }}</div>
                </div>
                <!-- Owner / assignee — staff only. Customers never saw this
                     field, so it stays absent in the customer view. -->
                <div v-if="isStaffView">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Owner
                    <span
                      v-if="fieldSaveState.owner"
                      class="ml-1 font-normal"
                      :class="fieldSaveState.owner === 'saved' ? 'text-emerald-600' : 'text-gray-400'"
                    >
                      <FontAwesomeIcon
                        :icon="['fas', saveIcon(fieldSaveState.owner)]"
                        :class="{ 'animate-spin': fieldSaveState.owner === 'saving' }"
                      />
                      {{ saveText(fieldSaveState.owner) }}
                    </span>
                  </label>
                  <Support_SearchableSelect
                    :model-value="item.owner ?? null"
                    :options="ownerOptions"
                    placeholder="Unassigned"
                    none-placeholder="Unassigned"
                    @update:model-value="setOwner"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Priority
                    <span
                      v-if="fieldSaveState.priority"
                      class="ml-1 font-normal"
                      :class="fieldSaveState.priority === 'saved' ? 'text-emerald-600' : 'text-gray-400'"
                    >
                      <FontAwesomeIcon
                        :icon="['fas', saveIcon(fieldSaveState.priority)]"
                        :class="{ 'animate-spin': fieldSaveState.priority === 'saving' }"
                      />
                      {{ saveText(fieldSaveState.priority) }}
                    </span>
                  </label>
                  <Support_PriorityRating
                    v-if="isStaffView"
                    :model-value="priorityValue"
                    :clearable="true"
                    @update:model-value="setPriority"
                  />
                  <div v-else class="text-sm">
                    <span v-if="priorityLabel" class="font-medium" :class="priorityColorClass">{{ priorityLabel }}</span>
                    <span v-else class="text-gray-400">Not set</span>
                  </div>
                </div>
                <!-- Status — staff only. The customer view keeps the header chip
                     (customer-friendly wording) as its single status surface. -->
                <div v-if="isStaffView">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Status
                    <span
                      v-if="fieldSaveState.status"
                      class="ml-1 font-normal"
                      :class="fieldSaveState.status === 'saved' ? 'text-emerald-600' : 'text-gray-400'"
                    >
                      <FontAwesomeIcon
                        :icon="['fas', saveIcon(fieldSaveState.status)]"
                        :class="{ 'animate-spin': fieldSaveState.status === 'saving' }"
                      />
                      {{ saveText(fieldSaveState.status) }}
                    </span>
                  </label>
                  <select
                    :value="item.status"
                    class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300"
                    @change="setStatus($event.target.value)"
                  >
                    <option v-for="s in statusOptions" :key="s.value" :value="s.value">
                      {{ s.label }}
                    </option>
                  </select>
                  <!-- Waiting is the one status whose customer-facing effect is
                       louder than its staff label suggests: it puts a "Waiting
                       on you" banner in front of the customer and asks them to
                       reply. Say so at the control, so nobody parks a ticket in
                       Waiting without meaning to send that. -->
                  <p
                    v-if="item.status === 'waiting'"
                    class="mt-1 text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1 flex items-start gap-1.5"
                  >
                    <FontAwesomeIcon :icon="['fas', 'circle-exclamation']" class="text-[10px] mt-0.5 shrink-0" />
                    <span>
                      The customer sees <strong>Needs Input</strong> and is asked to
                      reply. Use <strong>In Progress</strong> if you're the one
                      working on it.
                    </span>
                  </p>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">
                    Due Date
                    <span
                      v-if="fieldSaveState.due_date"
                      class="ml-1 font-normal"
                      :class="fieldSaveState.due_date === 'saved' ? 'text-emerald-600' : 'text-gray-400'"
                    >
                      <FontAwesomeIcon
                        :icon="['fas', saveIcon(fieldSaveState.due_date)]"
                        :class="{ 'animate-spin': fieldSaveState.due_date === 'saving' }"
                      />
                      {{ saveText(fieldSaveState.due_date) }}
                    </span>
                  </label>
                  <input
                    v-if="isStaffView"
                    type="date"
                    :value="dueDateInputValue"
                    class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-300"
                    :class="dueDateClass(item.due_date)"
                    @change="setDueDate($event.target.value)"
                  />
                  <div v-else class="text-sm" :class="dueDateClass(item.due_date)">
                    {{ formatDate(item.due_date) || 'Not set' }}
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Created</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.created_at) }}</div>
                </div>
                <div v-if="item.updated_at">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Updated</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.updated_at) }}</div>
                </div>
                <!-- SLA timestamps (internal reporting) — shown when the backend
                     provides them. -->
                <div v-if="isStaffView && item.first_response_at">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">First response</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.first_response_at) }}</div>
                </div>
                <div v-if="isStaffView && item.resolution_at">
                  <label class="block text-xs font-medium text-gray-500 mb-0.5">Resolved</label>
                  <div class="text-sm text-gray-600">{{ formatDate(item.resolution_at) }}</div>
                </div>
              </div>

              <!-- Ownership bar — who has the ball right now. Separate from the
                   stepper because ownership oscillates while progress doesn't.
                   Only meaningful mid-flight, so nothing renders in the Received
                   or Resolved stages. -->
              <div
                v-if="isCustomerView && ownership"
                role="status"
                class="rounded-lg border px-4 py-3"
                :class="ownership.theirBall
                  ? 'border-orange-200 bg-orange-50'
                  : 'border-gray-200 bg-white'"
              >
                <div class="flex items-start gap-2.5">
                  <FontAwesomeIcon
                    :icon="['fas', ownership.theirBall ? 'circle-exclamation' : 'gear']"
                    class="text-[13px] mt-0.5 shrink-0"
                    :class="ownership.theirBall ? 'text-orange-600' : 'text-gray-400'"
                  />
                  <div class="min-w-0">
                    <div
                      class="text-sm font-semibold"
                      :class="ownership.theirBall ? 'text-orange-900' : 'text-gray-700'"
                    >
                      {{ ownership.theirBall ? 'Waiting on you' : 'With the Kairon team' }}
                      <span v-if="ownership.sinceLabel" class="font-normal text-orange-700">
                        · {{ ownership.sinceLabel }}
                      </span>
                    </div>
                    <p
                      v-if="ownership.theirBall"
                      class="text-[12px] mt-0.5 text-orange-800"
                    >
                      Our team has a question for you — check the latest comment
                      below. We'll keep going as soon as you reply.
                    </p>
                    <p v-else class="text-[12px] mt-0.5 text-gray-500">
                      Our team is working on this. Nothing is needed from you —
                      we'll update you here as it moves.
                    </p>
                    <!-- Both routes a customer might reach for, side by side:
                         the composer they're already looking at, and the email
                         thread most of them actually reply in. Naming a route
                         without linking it ("reply by email") makes the reader
                         go find it. -->
                    <div v-if="ownership.theirBall" class="flex flex-wrap items-center gap-2 mt-2">
                      <button
                        type="button"
                        class="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded px-2.5 py-1 transition-colors"
                        @click="focusCommentComposer"
                      >
                        <FontAwesomeIcon :icon="['fas', 'comment']" class="text-[10px]" />
                        Reply here
                      </button>
                      <a
                        :href="ticketMailtoHref"
                        class="inline-flex items-center gap-1.5 text-xs font-medium text-orange-800 hover:text-orange-900 underline underline-offset-2"
                      >
                        <FontAwesomeIcon :icon="['fas', 'envelope']" class="text-[10px]" />
                        Reply by email instead
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Customer lifecycle stepper + SLA expectation (best-in-class) -->
              <div v-if="isCustomerView" class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div class="flex items-center">
                  <template v-for="(step, i) in customerSteps" :key="step.key">
                    <div class="flex flex-col items-center text-center">
                      <!-- The tick / number / alert glyph is the only thing
                           distinguishing done from current from pending, and
                           none of it has a text equivalent — hence the spelled
                           out accessible name — which needs role="img" to be
                           exposed at all, since a bare <span> is role=generic
                           and generic elements cannot be named. -->
                      <span
                        class="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold"
                        :class="step.attention ? 'bg-orange-500 text-white' : step.done ? 'bg-green-500 text-white' : step.current ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-400'"
                        :title="step.hint"
                        role="img"
                        :aria-label="`Step ${i + 1} of ${customerSteps.length}: ${step.label} — ${step.hint}`"
                      >
                        <FontAwesomeIcon v-if="step.attention" :icon="['fas', 'circle-exclamation']" class="text-[11px]" />
                        <FontAwesomeIcon v-else-if="step.done" :icon="['fas', 'check']" class="text-[10px]" />
                        <span v-else>{{ i + 1 }}</span>
                      </span>
                      <span
                        class="text-[11px] font-medium mt-1"
                        :class="step.attention ? 'text-orange-800' : step.current || step.done ? 'text-gray-800' : 'text-gray-400'"
                      >{{ step.label }}</span>
                      <span v-if="step.at" class="text-[10px] text-gray-400">{{ formatDate(step.at) }}</span>
                    </div>
                    <div
                      v-if="i < customerSteps.length - 1"
                      class="flex-1 h-0.5 mx-2"
                      :class="customerSteps[i + 1].done || customerSteps[i + 1].current ? 'bg-green-400' : 'bg-gray-200'"
                    />
                  </template>
                </div>
                <!-- The SLA promise only applies while the ball is ours —
                     "we respond within 1 business day" is not a commitment we
                     can make when we're the ones waiting. -->
                <p class="text-[11px] text-gray-500 mt-3 flex items-center gap-1.5">
                  <FontAwesomeIcon :icon="['fas', 'clock']" class="text-[10px]" />
                  {{ needsCustomerInput
                    ? "We'll pick this straight back up once you reply."
                    : 'We typically respond within 1 business day.' }}
                </p>
              </div>

              <!-- Description -->
              <div v-if="item.description">
                <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  Description
                  <span v-if="isStaffView" class="inline-flex items-center gap-1 text-emerald-600 font-normal">
                    <FontAwesomeIcon :icon="['fas', 'eye']" class="text-[10px]" />
                    Visible to the customer
                  </span>
                </label>
                <div class="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg px-4 py-3">{{ item.description }}</div>
              </div>

              <!-- Tags (staff only -- internal work-stream labels) -->
              <div v-if="isStaffView">
                <label class="block text-xs font-medium text-gray-500 mb-1">Tags</label>
                <Support_TagEditor
                  :model-value="item.tags || []"
                  @update:model-value="setTags"
                />
              </div>

              <!-- Internal notes (staff only -- never shown to customers) -->
              <div v-if="isStaffView">
                <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <FontAwesomeIcon :icon="['fas', 'lock']" class="text-[10px] text-amber-600" />
                  Internal notes
                  <span class="text-amber-600 font-normal">Only your team can see this — never shown to customers</span>
                  <span v-if="internalNotesSaveState === 'saving'" class="text-gray-400 font-normal ml-auto">
                    <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-1" />Saving...
                  </span>
                  <span v-else-if="internalNotesSaveState === 'saved'" class="text-emerald-600 font-normal ml-auto">
                    <FontAwesomeIcon :icon="['fas', 'check']" class="mr-1" />Saved
                  </span>
                </label>
                <textarea
                  ref="internalNotesTextarea"
                  v-model="item.internal_notes"
                  rows="2"
                  placeholder="Add an internal note..."
                  class="w-full text-sm text-gray-800 whitespace-pre-wrap bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none overflow-y-auto max-h-48"
                  @input="autoResizeInternalNotes"
                  @blur="saveInternalNotes"
                ></textarea>
              </div>

              <!-- Attachments (new RequirementMedia model). Attachments are
                   customer-visible content, so the uploader is NOT gated on
                   isStaffView — a customer opening their own ticket needs to
                   be able to attach a screenshot without hunting for the
                   comment composer. -->
              <div v-if="item.id">
                <label class="block text-xs font-medium text-gray-500 mb-1">Attachments</label>
                <RequirementAttachmentList
                  v-if="requirementMediaFiles.length"
                  :files="requirementMediaFiles"
                  :use-media-endpoint="true"
                  :removable="canDeleteAttachments"
                  class="mb-2"
                  @remove="removeAttachment"
                />
                <!-- Legacy metadata fallback, for tickets that predate the
                     RequirementMedia model. -->
                <RequirementAttachmentList
                  v-else-if="legacyAttachments.length"
                  :files="legacyAttachments"
                  class="mb-2"
                />
                <RequirementAttachmentUpload
                  ref="attachUpload"
                  :requirement-id="item.id"
                  :surface="true"
                  :drop-label="`to ticket #${item.id}`"
                  @uploaded="onAttachmentUploaded"
                />
              </div>

              <!-- Comment Thread (new API) -->
              <div class="border-t border-gray-200 pt-5">
                <label class="text-sm font-medium text-gray-700 mb-3 block">Comments</label>
                <RequirementCommentThread
                  v-if="item.id"
                  ref="commentThread"
                  :requirement-id="item.id"
                  :force-customer-view="isCustomerView"
                  @comment-count="onNewCommentCount"
                />
              </div>

              <!-- Legacy comments (old requirement-as-comment model = internal
                   staff sidebar notes). Staff only -- never shown to customers. -->
              <div v-if="legacyComments.length && !isCustomerView" class="border-t border-gray-200 pt-5">
                <label class="text-sm font-medium text-gray-700 mb-3 block">
                  Previous Comments
                  <span class="text-xs text-gray-400 font-normal ml-1">{{ legacyComments.length }}</span>
                </label>
                <div class="space-y-2.5">
                  <div
                    v-for="c in legacyComments"
                    :key="c.id"
                    class="bg-gray-50 rounded-lg border border-gray-200 px-3 py-2.5"
                  >
                    <div class="text-sm text-gray-800 whitespace-pre-wrap">{{ c.description }}</div>
                    <div v-if="legacyCommentAttachments(c).length" class="mt-1.5 space-y-1">
                      <div
                        v-for="(file, fi) in legacyCommentAttachments(c)"
                        :key="fi"
                        class="flex items-center text-xs"
                      >
                        <FontAwesomeIcon :icon="['fas', fileIcon(file.file_type)]" class="text-gray-400 mr-1.5 shrink-0" />
                        <a
                          :href="'/api/media/?key=' + encodeURIComponent(file.file_key)"
                          target="_blank"
                          class="text-blue-600 hover:text-blue-800 truncate"
                        >
                          {{ file.filename }}
                        </a>
                      </div>
                    </div>
                    <div class="flex items-center mt-1.5">
                      <span class="text-[10px] text-gray-400">
                        {{ c.owner_name || 'Unknown' }} · {{ relativeTime(c.created_at) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activity (internal staff audit trail: raw status/owner/priority
                   churn — hidden from customers) -->
              <div v-if="!isCustomerView" class="border-t border-gray-200 pt-5">
                <label class="text-sm font-medium text-gray-700 mb-2.5 block">Activity</label>
                <div v-if="activities.length" class="space-y-2.5">
                  <div
                    v-for="a in activities"
                    :key="a.id"
                    class="flex gap-2 text-sm"
                  >
                    <div
                      class="mt-0.5"
                      :class="a.activity_type === 'commented' ? 'text-primary-500' : 'text-gray-400'"
                    >
                      <FontAwesomeIcon :icon="activityIcon(a.activity_type)" class="w-3.5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <span class="text-gray-700" v-html="formatActivity(a)"></span>
                      <div
                        v-if="activityCommentPreview(a)"
                        class="mt-1 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5 whitespace-pre-wrap line-clamp-3"
                      >{{ activityCommentPreview(a) }}</div>
                      <div class="text-xs text-gray-400 mt-0.5">{{ relativeTime(a.created_at) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-400">No activity yet</div>
              </div>
            </div>
          </div>
        </template>
      </div>
  </SlideOverTray>
  <!-- Sibling of the tray, not inside it: the undo window can outlive the
       drawer being closed, and a toast that unmounts mid-window would
       strand the edit. -->
  <Support_UndoToast ref="undoToast" />
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { useToast } from 'vue-toastification';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SlideOverTray from './SlideOverTray.vue';
import SourceBadge from './SourceBadge.vue';
import TriagePanel from './TriagePanel.vue';
import RequirementCommentThread from './RequirementCommentThread.vue';
import RequirementAttachmentList from './RequirementAttachmentList.vue';
import RequirementAttachmentUpload from './RequirementAttachmentUpload.vue';
import Support_UndoToast from './Support_UndoToast.vue';
import Support_PriorityRating from './Support_PriorityRating.vue';
import Support_SearchableSelect from './Support_SearchableSelect.vue';
import Support_TagEditor from './Support_TagEditor.vue';
import {
  customerStatusLabel,
  customerStatusClass,
  customerStatusHint,
  customerTypeLabel,
  customerBucket,
  isSelectableStatus,
  SELECTABLE_STATUSES,
  STATUS_LABELS,
  formatDateValue,
  daysUntil,
  daysAgoLabel,
  PRIORITY_LABELS,
  PRIORITY_INT_TO_CODE,
} from './support_constants';

// Human wording for the failure toast — "Couldn't save the due date" beats
// "Failed to update due_date".
const FIELD_LABELS = {
  due_date: 'due date',
  priority: 'priority',
  status: 'status',
  owner: 'owner',
};

const STATUS_CLASSES = {
  approved: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  open: 'bg-red-50 text-red-700',
  waiting: 'bg-orange-50 text-orange-700',
  done: 'bg-green-50 text-green-700',
  resolved: 'bg-green-50 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

const TYPE_CLASSES = {
  Epic: 'bg-indigo-50 text-indigo-700',
  Feature: 'bg-blue-50 text-blue-700',
  Bug: 'bg-red-50 text-red-700',
  'Support Ticket': 'bg-amber-50 text-amber-700',
};

const PRIORITY_MAP = { p0: 0, p1: 1, p2: 2, p3: 3, none: null };

export default {
  name: 'Support_ItemDetail',
  components: {
    FontAwesomeIcon,
    SlideOverTray,
    SourceBadge,
    TriagePanel,
    RequirementCommentThread,
    RequirementAttachmentList,
    RequirementAttachmentUpload,
    Support_PriorityRating,
    Support_SearchableSelect,
    Support_UndoToast,
    Support_TagEditor,
  },
  props: {
    itemId: { type: [String, Number], default: null },
    // When a staff user is in "Customer view" preview, force the detail to the
    // customer view too so internal notes / activity / staff badges never leak
    // on a screen-share.
    forceCustomerView: { type: Boolean, default: false },
    // Assignable users, already loaded by the parent list.
    users: { type: Array, default: () => [] },
  },
  emits: ['close', 'comment-count', 'triaged', 'updated'],
  data() {
    return {
      item: {},
      loading: false,
      legacyComments: [],
      commentsLoading: false,
      commentTypeId: null,
      activities: [],
      requirementMediaFiles: [],
      _firstEmailSender: null,
      internalNotesSaveState: null,
      showCustomerLegend: false,
      idCopied: false,
      replyAddressCopied: false,
      // Per-field save feedback for the editable meta fields, keyed by field
      // name -> 'saving' | 'saved'. Mirrors internalNotesSaveState so every
      // editable thing in the drawer confirms itself the same way.
      fieldSaveState: {},
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    toast() {
      return useToast();
    },
    isStaff() {
      return this.current_user?.is_staff || this.current_user?.is_superuser;
    },
    isCustomerView() {
      // Mirrors isStaff (is_staff || is_superuser) and Support_ServiceDesk's
      // staff predicate, so the customer/staff split is consistent everywhere.
      // forceCustomerView lets a staff preview render the customer view.
      return !this.isStaff || this.forceCustomerView;
    },
    isStaffView() {
      // Effective staff view: real staff AND not previewing as a customer.
      return !this.isCustomerView;
    },
    isInternalOnly() {
      return this.item.customer_facing === false;
    },
    // Mirrors RequirementMediaViewSet's destroy gate ({ADMIN, SECURITY}) so we
    // never render a delete control that would 403.
    canDeleteAttachments() {
      return (
        this.current_user?.is_superuser ||
        this.$store.getters['tenant/isTenantAdmin'] ||
        this.$store.getters['tenant/isSecurityUser']
      );
    },
    legacyAttachments() {
      return this.item.metadata?.attachments || [];
    },
    firstEmailSender() {
      return this._firstEmailSender;
    },
    priorityValue() {
      if (typeof this.item.priority === 'number') return this.item.priority;
      return PRIORITY_MAP[this.item.priority] ?? null;
    },
    priorityLabel() {
      if (this.priorityValue == null) return null;
      return PRIORITY_LABELS[PRIORITY_INT_TO_CODE[this.priorityValue]] || `P${this.priorityValue}`;
    },
    priorityColorClass() {
      const map = {
        0: 'text-red-700',
        1: 'text-orange-700',
        2: 'text-amber-700',
        3: 'text-gray-600',
      };
      return map[this.priorityValue] || 'text-gray-600';
    },
    // Staff-only edit affordances below. Each is only rendered when isStaffView
    // is true, so customers (and staff previewing as a customer) see the
    // unchanged read-only labels.
    statusOptions() {
      const values = SELECTABLE_STATUSES.filter(isSelectableStatus);
      const current = this.item.status;
      if (current && !values.includes(current)) values.push(current);
      return values.map((value) => ({ value, label: STATUS_LABELS[value] || value }));
    },
    ownerOptions() {
      // The user serializer exposes full_name, not name — `u.name` is
      // undefined and silently falls through to the raw email address.
      return this.users.map((u) => ({ id: u.id, title: u.full_name || u.email }));
    },
    dueDateInputValue() {
      // Backend may hand back a date or a datetime; <input type="date"> wants
      // YYYY-MM-DD.
      return this.item.due_date ? String(this.item.due_date).slice(0, 10) : '';
    },
    ticketMailtoHref() {
      // Prefilled so Pylon can thread by ticket number (item 6).
      const subject = `Ticket #${this.item.id} — ${this.item.title || ''}`.trim();
      return `mailto:support@kaironhealth.com?subject=${encodeURIComponent(subject)}`;
    },
    customerStatusBucket() {
      return customerBucket(this.item.status);
    },
    // The ball is with the customer. Distinct from "in progress" — same stage,
    // opposite ownership.
    needsCustomerInput() {
      return this.customerStatusBucket === 'waiting';
    },
    // Who owns the ticket right now, or null outside the Active stage (nothing
    // to say while it's freshly Received or already Resolved).
    //
    // waiting_since is OPTIONAL: it ships in a parallel newCoBackend PR and is
    // permanently null for tickets that were already Waiting before that
    // migration. sinceLabel is '' in that case and the clause is omitted.
    ownership() {
      const bucket = this.customerStatusBucket;
      if (bucket !== 'in_progress' && bucket !== 'waiting') return null;
      const theirBall = bucket === 'waiting';
      let sinceLabel = '';
      if (theirBall && this.item.waiting_since) {
        const elapsed = daysAgoLabel(this.item.waiting_since);
        const date = formatDateValue(this.item.waiting_since, { month: 'short', day: 'numeric' });
        if (date) sinceLabel = elapsed ? `since ${date} · ${elapsed}` : `since ${date}`;
      }
      return { theirBall, sinceLabel };
    },
    // Customer-facing lifecycle stepper (best-in-class): Received -> Active ->
    // Resolved. THREE nodes, deliberately: progress is linear and monotonic,
    // while ownership (us / them) oscillates within the Active stage. A fourth
    // node ordered against the other three is true for only one of the two
    // cases -- a ticket that needs input BEFORE work starts would render "In
    // Progress" as completed. So 'waiting' maps onto the middle stage and only
    // changes that node's label and styling; the ownership bar above carries
    // the orthogonal dimension. Stage identity is POSITION, so the label flip
    // is not a regression.
    //
    // Per-stage timestamps come from the backend `lifecycle` object (contract)
    // with sensible fallbacks so it renders even before that lands. The middle
    // node keeps reading in_progress_at, which the backend stamps on entry to
    // either sub-state.
    customerSteps() {
      const order = ['received', 'in_progress', 'resolved'];
      const bucket = this.customerStatusBucket;
      const needsInput = this.needsCustomerInput;
      // 'waiting' is the Active stage, not a stage of its own — resolve it to
      // 'in_progress' before the lookup so indexOf can't miss and silently
      // fall back to index 0 ("Received").
      const stage = needsInput ? 'in_progress' : bucket;
      const currentIndex = Math.max(order.indexOf(stage), 0);
      const lifecycle = this.item.lifecycle || {};
      const at = {
        received: lifecycle.received_at || this.item.created_at,
        in_progress: lifecycle.in_progress_at,
        resolved: lifecycle.resolved_at || this.item.completed_at,
      };
      const labels = {
        received: 'Received',
        in_progress: needsInput ? 'Needs Input' : 'In Progress',
        resolved: 'Resolved',
      };
      return order.map((key, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        const attention = needsInput && key === 'in_progress';
        let hint;
        if (attention) hint = 'Waiting on you — we need information before we can continue';
        else if (done) hint = 'Done';
        else if (current) hint = customerStatusHint(key);
        else hint = 'Not yet';
        return {
          key,
          label: labels[key],
          at: at[key] || null,
          done,
          current,
          attention,
          hint,
        };
      });
    },
  },
  watch: {
    itemId(newId) {
      if (newId) {
        this.load();
      } else {
        this.reset();
      }
    },
  },
  mounted() {
    if (this.itemId) this.load();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeCustomerLegend);
  },
  methods: {
    // The ownership bar's call to action: scroll the composer into view and put
    // the cursor in it, so "add a comment below" is one click rather than a
    // scroll hunt.
    focusCommentComposer() {
      this.$refs.commentThread?.focusComposer();
    },
    _copyToClipboard(text, flagName) {
      if (!navigator.clipboard?.writeText) {
        this.toast.error('Could not copy to clipboard.');
        return;
      }
      return navigator.clipboard.writeText(text).then(() => {
        this[flagName] = true;
        setTimeout(() => { this[flagName] = false; }, 1200);
      }).catch(() => {
        this.toast.error('Could not copy to clipboard.');
      });
    },
    copyTicketId() {
      return this._copyToClipboard(String(this.item.id), 'idCopied');
    },
    copyReplyAddress() {
      return this._copyToClipboard(this.item.reply_address, 'replyAddressCopied');
    },
    toggleCustomerLegend() {
      this.showCustomerLegend = !this.showCustomerLegend;
      if (this.showCustomerLegend) {
        this.$nextTick(() => document.addEventListener('click', this.closeCustomerLegend));
      } else {
        document.removeEventListener('click', this.closeCustomerLegend);
      }
    },
    closeCustomerLegend() {
      this.showCustomerLegend = false;
      document.removeEventListener('click', this.closeCustomerLegend);
    },
    autoResizeInternalNotes(e) {
      const el = e?.target || this.$refs.internalNotesTextarea;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    async saveInternalNotes() {
      if (!this.item.id) return;
      this.internalNotesSaveState = 'saving';
      try {
        await axios.patch(`/api/requirements/${this.item.id}/`, { internal_notes: this.item.internal_notes });
        this.internalNotesSaveState = 'saved';
        setTimeout(() => { if (this.internalNotesSaveState === 'saved') this.internalNotesSaveState = null; }, 2000);
      } catch {
        this.internalNotesSaveState = null;
        this.toast.error('Failed to save internal note.');
      }
    },
    // Shared write path for the staff-editable meta fields.
    //
    // Deferred-commit, exactly like the table's setPriority: apply the change
    // locally, tell the parent so the row moves immediately, and show an UNDO
    // toast. The request only goes out when the toast settles (5s, or on
    // dismiss / unmount). Undo reverts locally and never sends anything. The
    // same edit made from the table and from the drawer now behaves the same
    // way, which is the point -- one action shouldn't have two safety models.
    writeField({ field, payload, local, url, message }) {
      if (!this.isStaffView || !this.item.id) return;
      // Capture identity now: the toast can settle after the drawer has moved
      // to another item or closed.
      const id = this.item.id;
      const prev = {};
      for (const key of Object.keys(local)) prev[key] = this.item[key];

      this.applyLocal(id, local);

      const commit = async () => {
        this.setFieldState(field, 'saving', id);
        try {
          const { data } = await axios.patch(url || `/api/requirements/${id}/`, payload);
          // Reconcile only the fields we touched plus the ones the backend
          // derives from them — merging the whole payload could clobber
          // internal_notes mid-edit.
          const changed = { ...local };
          if (data && typeof data === 'object') {
            for (const key of [...Object.keys(local), 'updated_at', 'completed_at', 'lifecycle']) {
              if (key in data) changed[key] = data[key];
            }
          }
          this.applyLocal(id, changed);
          if (field === 'status') this.loadHistory();
          this.setFieldState(field, 'saved', id);
          setTimeout(() => {
            if (this.fieldSaveState[field] === 'saved') this.setFieldState(field, null, id);
          }, 2000);
        } catch (e) {
          // Rethrow so the toast surfaces "Save failed" with RETRY rather
          // than silently dropping the edit.
          this.setFieldState(field, null, id);
          throw e;
        }
      };

      // No toast mounted (shouldn't happen, but the alternative is throwing
      // after the optimistic write and stranding the field mid-edit with
      // nothing ever sent) — just commit straight away.
      if (!this.$refs.undoToast) {
        commit().catch(() => {
          this.applyLocal(id, prev);
          this.toast.error(`Couldn't save the ${FIELD_LABELS[field] || field}. Your change was undone.`);
        });
        return;
      }

      this.$refs.undoToast.show(message, commit, () => {
        this.applyLocal(id, prev);
        this.setFieldState(field, null, id);
      });
    },
    // Writes to the drawer's copy (only if it's still showing that item) and
    // always tells the parent list, which owns the row regardless.
    applyLocal(id, changes) {
      if (this.item?.id === id) Object.assign(this.item, changes);
      this.$emit('updated', { id, changes });
    },
    // Only 'saving' and 'saved'. There is deliberately no indicator while the
    // undo window is open: the toast on screen already says what changed and
    // offers UNDO, and a second "Undo available" label next to the field just
    // sat there for the full 5 seconds and read as a hung save.
    saveIcon(state) {
      return state === 'saved' ? 'check' : 'spinner';
    },
    saveText(state) {
      return state === 'saved' ? 'Saved' : 'Saving...';
    },
    // `forId` scopes the badge to the item the edit belongs to. A commit can
    // settle after the drawer has moved on, and clearing fieldSaveState in
    // reset() isn't enough on its own — the in-flight commit would just write
    // "Saved" onto whichever item is showing now, crediting an edit the user
    // never made there.
    setFieldState(field, state, forId) {
      if (forId != null && this.item?.id !== forId) return;
      this.fieldSaveState = { ...this.fieldSaveState, [field]: state };
    },
    setTags(newTags) {
      this.writeField({
        field: 'tags',
        payload: { tags: newTags },
        local: { tags: newTags },
        message: 'Tags updated',
      });
    },
    setDueDate(value) {
      const next = value || null;
      if (next === (this.item.due_date || null)) return;
      this.writeField({
        field: 'due_date',
        payload: { due_date: next },
        local: { due_date: next },
        message: next ? `Due date set to ${formatDateValue(next)}` : 'Due date cleared',
      });
    },
    setPriority(value) {
      if (value === this.priorityValue) return;
      // Mirrors Support_ServiceDesk's setPriority: the widget speaks 0-3/null,
      // the API expects a p-code and uses 'none' to clear.
      this.writeField({
        field: 'priority',
        payload: { priority: value ?? 'none' },
        local: { priority: value },
        message: value != null ? `Priority set to P${value}` : 'Priority cleared',
      });
    },
    setStatus(status) {
      if (!status || status === this.item.status) return;
      // Routed through the set-status action rather than a bare PATCH. Both
      // log a typed status activity (perform_update does the same branching),
      // so that isn't the reason -- set-status is the narrower endpoint: it
      // enforces CUSTOMER_SETTABLE_STATUSES and the admin-only archive gate,
      // so the same call works if status editing is ever opened to customers
      // without granting them full edit rights.
      this.writeField({
        field: 'status',
        payload: { status },
        local: { status },
        url: `/api/requirements/${this.item.id}/set-status/`,
        message: `Status set to ${STATUS_LABELS[status] || status}`,
      });
    },
    setOwner(ownerId) {
      if ((ownerId ?? null) === (this.item.owner ?? null)) return;
      const user = this.users.find((u) => u.id === ownerId);
      const name = user ? user.full_name || user.email : null;
      this.writeField({
        field: 'owner',
        payload: { owner: ownerId },
        local: { owner: ownerId, owner_name: name },
        message: name ? `Assigned to ${name}` : 'Owner cleared',
      });
    },
    async load() {
      this.reset();
      this.loading = true;
      await Promise.allSettled([
        this.loadItem(),
        this.loadRequirementTypes(),
      ]);
      this.loading = false;
      this.$nextTick(() => this.autoResizeInternalNotes());
      await Promise.allSettled([
        this.loadLegacyComments(),
        this.loadHistory(),
        this.loadRequirementMedia(),
        this.loadFirstEmailSender(),
      ]);
    },
    reset() {
      this.item = {};
      this.legacyComments = [];
      this.activities = [];
      this.requirementMediaFiles = [];
      this._firstEmailSender = null;
      // Per-field badges belong to the item we're leaving. A commit settling
      // after the switch would otherwise flash "Saved" on the new item's
      // field, crediting an edit the user never made here. The in-flight write
      // still completes — it targets a captured id, not this.item.
      this.fieldSaveState = {};
    },
    async loadItem() {
      try {
        const { data } = await axios.get(`/api/requirements/${this.itemId}/`);
        this.item = data;
      } catch {
        this.toast.error('Item not found.');
        this.$emit('close');
      }
    },
    async loadRequirementTypes() {
      if (this.commentTypeId) return;
      try {
        const { data } = await axios.get('/api/requirement-types/');
        const commentType = data.find((t) => t.name === 'Comment');
        if (commentType) this.commentTypeId = commentType.id;
      } catch {
        // ignore
      }
    },
    async loadLegacyComments() {
      if (this.isCustomerView || !this.item?.id || !this.commentTypeId) return;
      this.commentsLoading = true;
      try {
        const { data } = await axios.get('/api/requirements/', {
          params: {
            parent: this.item.id,
            requirement_type: this.commentTypeId,
          },
        });
        this.legacyComments = data;
      } catch {
        this.legacyComments = [];
      } finally {
        this.commentsLoading = false;
      }
    },
    async loadRequirementMedia() {
      if (!this.item?.id) return;
      try {
        const { data } = await axios.get('/api/requirement-media/', {
          params: { requirement: this.item.id },
        });
        this.requirementMediaFiles = Array.isArray(data) ? data : data.results || [];
      } catch {
        this.requirementMediaFiles = [];
      }
    },
    async removeAttachment(file) {
      const name = file.filename || file.file_name || 'this attachment';
      if (!window.confirm(`Remove ${name} from this ticket? This can't be undone.`)) {
        return;
      }
      try {
        await axios.delete(`/api/requirement-media/${file.id}/`);
        await this.loadRequirementMedia();
        this.toast.success('Attachment removed.');
      } catch {
        this.toast.error('Could not remove that attachment.');
      }
    },
    async onAttachmentUploaded() {
      // Pull the file into the list above so it shows without a reload, then
      // drop the staged row so it isn't listed twice. Only clear once nothing
      // is still in flight — a multi-file drop finishes one at a time.
      await this.loadRequirementMedia();
      const uploader = this.$refs.attachUpload;
      if (uploader && !uploader.hasPending()) uploader.clear();
    },
    async loadFirstEmailSender() {
      if (!this.item?.id || !this.item?.needs_triage) return;
      try {
        const { data } = await axios.get('/api/requirement-comments/', {
          params: { requirement: this.item.id, limit: 1, offset: 0 },
        });
        const comments = data.results || data;
        const emailComment = comments.find((c) => c.email_sender);
        if (emailComment) this._firstEmailSender = emailComment.email_sender;
      } catch {
        // ignore
      }
    },
    onNewCommentCount(count) {
      this.$emit('comment-count', { id: this.item.id, count });
    },
    onTriaged(data) {
      this.item.needs_triage = false;
      this.item.tenant = data.tenantId;
      this.item.tenant_name = data.tenantName;
      this.item.requester = data.requesterId;
      this.item.requester_name = data.requesterName;
      this.$emit('triaged', { id: this.item.id, ...data });
    },
    legacyCommentAttachments(comment) {
      return comment.metadata?.attachments || [];
    },
    async loadHistory() {
      if (!this.item?.id) return;
      try {
        const { data } = await axios.get(
          `/api/requirements/${this.item.id}/history/`,
        );
        this.activities = data;
      } catch {
        this.activities = [];
      }
    },
    activityCommentPreview(a) {
      if (a.activity_type !== 'commented') return '';
      return a.metadata?.body || a.description || '';
    },
    activityIcon(type) {
      const icons = {
        created: ['fas', 'plus'],
        updated: ['fas', 'pencil'],
        status_changed: ['fas', 'arrow-right'],
        assigned: ['fas', 'user'],
        completed: ['fas', 'check'],
        reopened: ['fas', 'rotate-left'],
        linked: ['fas', 'link'],
        unlinked: ['fas', 'link-slash'],
        reordered: ['fas', 'arrows-up-down'],
        commented: ['fas', 'comment'],
      };
      return icons[type] || ['fas', 'circle'];
    },
    formatActivity(a) {
      const name = `<span class="font-medium text-gray-900">${this.escapeHtml(a.user_name || 'System')}</span>`;
      const changes = a.field_changes || [];
      switch (a.activity_type) {
        case 'created':
          return `${name} created this`;
        case 'completed':
          return `${name} marked this complete`;
        case 'reopened':
          return `${name} reopened this`;
        case 'reordered':
          return `${name} reordered this`;
        case 'linked':
          return `${name} linked to ${this.escapeHtml(a.metadata?.target_title || 'item')} (${this.escapeHtml(a.metadata?.link_type || 'related')})`;
        case 'unlinked':
          return `${name} removed link to ${this.escapeHtml(a.metadata?.target_title || 'item')}`;
        case 'assigned': {
          const assignee = changes.find((c) => c.field === 'owner');
          return assignee
            ? `${name} assigned to ${this.escapeHtml(assignee.new_value || 'Unassigned')}`
            : `${name} changed assignment`;
        }
        case 'status_changed': {
          const sc = changes.find((c) => c.field === 'status');
          if (a.description) {
            const description = this.escapeHtml(a.description);
            return sc
              ? `${name} — ${description} (${this.escapeHtml(sc.old_value)} -> ${this.escapeHtml(sc.new_value)})`
              : `${name} — ${description}`;
          }
          return sc
            ? `${name} changed status from ${this.escapeHtml(sc.old_value)} to ${this.escapeHtml(sc.new_value)}`
            : `${name} changed status`;
        }
        case 'updated': {
          if (!changes.length) return `${name} updated this`;
          return changes
            .map(
              (c) =>
                `${name} changed ${this.escapeHtml(c.field)} from ${this.escapeHtml(c.old_value || 'empty')} to ${this.escapeHtml(c.new_value || 'empty')}`,
            )
            .join('<br>');
        }
        case 'commented': {
          const src = a.metadata?.source;
          const badge =
            src === 'internal_note'
              ? ' <span class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 ml-1">Internal</span>'
              : '';
          return `${name} commented${badge}`;
        }
        default:
          return `${name} ${this.escapeHtml(a.activity_type_display || a.activity_type)}`;
      }
    },
    escapeHtml(str) {
      if (!str) return '';
      const s = String(str);
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    typeClass(type) {
      return TYPE_CLASSES[type] || 'bg-gray-100 text-gray-600';
    },
    typeLabel(type) {
      if (this.isCustomerView) return customerTypeLabel(type);
      return type;
    },
    statusClass(status) {
      if (this.isCustomerView) return customerStatusClass(status);
      return STATUS_CLASSES[status] || 'bg-gray-100 text-gray-500';
    },
    statusLabel(status) {
      if (this.isCustomerView) return customerStatusLabel(status);
      return STATUS_LABELS[status] || status;
    },
    // Plain-language gloss, doubling as the chip's accessible name so its
    // meaning doesn't ride on colour alone.
    statusTitle(status) {
      if (this.isCustomerView) return customerStatusHint(status);
      return STATUS_LABELS[status] || status;
    },
    // Accessible name for the header chip. Only the customer chip gets the
    // gloss appended — in the staff view statusTitle() is the label itself, so
    // concatenating would read the status twice ("Waiting. Waiting").
    statusAriaLabel(status) {
      const label = this.statusLabel(status);
      const hint = this.statusTitle(status);
      return !this.isCustomerView || !hint || hint === label ? label : `${label}. ${hint}`;
    },
    formatDate(dateStr) {
      return formatDateValue(dateStr);
    },
    relativeTime(dateStr) {
      if (!dateStr) return '';
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return this.formatDate(dateStr);
    },
    dueDateClass(date) {
      const diffDays = daysUntil(date);
      if (diffDays == null) return 'text-gray-400';
      if (diffDays < 0) return 'text-red-600 font-medium';
      if (diffDays <= 3) return 'text-amber-600';
      return 'text-gray-600';
    },
    fileIcon(mimeType) {
      if (mimeType?.includes('pdf')) return 'file-pdf';
      if (mimeType?.includes('image')) return 'file-image';
      if (mimeType?.includes('word')) return 'file-word';
      if (
        mimeType?.includes('sheet') ||
        mimeType?.includes('excel')
      )
        return 'file-excel';
      if (
        mimeType?.includes('presentation') ||
        mimeType?.includes('powerpoint')
      )
        return 'file-powerpoint';
      return 'file';
    },
  },
};
</script>
